/**
 * Extracts video ID and provider from various video platform URLs.
 * Supports: YouTube, Vimeo, Dailymotion, Facebook, Twitch, Wistia, and more.
 *
 * Returns { provider, videoId, embedUrl, thumbnailUrl } or null if unrecognized.
 */

const VIDEO_PATTERNS = [
  // YouTube - standard, short, embed, live, shorts
  {
    provider: 'youtube',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube-nocookie\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    ],
    getEmbedUrl: (id) => `https://www.youtube.com/embed/${id}`,
    getThumbnailUrl: (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
  },
  // Vimeo - standard, player embed, channel, groups, showcase
  {
    provider: 'vimeo',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
      /(?:https?:\/\/)?player\.vimeo\.com\/video\/(\d+)/,
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/channels\/\w+\/(\d+)/,
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/groups\/\w+\/videos\/(\d+)/,
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/showcase\/\w+\/video\/(\d+)/,
    ],
    getEmbedUrl: (id) => `https://player.vimeo.com/video/${id}`,
    getThumbnailUrl: () => null, // Vimeo thumbnails require API call
  },
  // Dailymotion
  {
    provider: 'dailymotion',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)/,
      /(?:https?:\/\/)?dai\.ly\/([a-zA-Z0-9]+)/,
      /(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/embed\/video\/([a-zA-Z0-9]+)/,
    ],
    getEmbedUrl: (id) => `https://www.dailymotion.com/embed/video/${id}`,
    getThumbnailUrl: (id) => `https://www.dailymotion.com/thumbnail/video/${id}`,
  },
  // Facebook video
  {
    provider: 'facebook',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?facebook\.com\/(?:watch\/?\?v=|.*\/videos\/)(\d+)/,
      /(?:https?:\/\/)?(?:www\.)?fb\.watch\/([a-zA-Z0-9_-]+)/,
    ],
    getEmbedUrl: (id, originalUrl) => `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(originalUrl)}`,
    getThumbnailUrl: () => null,
  },
  // Twitch - clips and videos
  {
    provider: 'twitch',
    patterns: [/(?:https?:\/\/)?(?:www\.)?twitch\.tv\/videos\/(\d+)/, /(?:https?:\/\/)?clips\.twitch\.tv\/([a-zA-Z0-9_-]+)/],
    getEmbedUrl: (id, originalUrl) => {
      if (originalUrl.includes('clips.twitch.tv')) {
        return `https://clips.twitch.tv/embed?clip=${id}&parent=${window.location.hostname}`
      }
      return `https://player.twitch.tv/?video=${id}&parent=${window.location.hostname}`
    },
    getThumbnailUrl: () => null,
  },
  // Wistia
  {
    provider: 'wistia',
    patterns: [
      /(?:https?:\/\/)?(?:[\w-]+\.)?wistia\.com\/medias\/([a-zA-Z0-9]+)/,
      /(?:https?:\/\/)?fast\.wistia\.net\/embed\/iframe\/([a-zA-Z0-9]+)/,
    ],
    getEmbedUrl: (id) => `https://fast.wistia.net/embed/iframe/${id}`,
    getThumbnailUrl: () => null,
  },
]

/**
 * Parse a video URL and return structured info for embedding.
 * @param {string} url - The video URL to parse
 * @returns {{ provider: string, videoId: string, embedUrl: string, thumbnailUrl: string|null } | null}
 */
export const parseVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return null

  const trimmedUrl = url.trim()

  for (const config of VIDEO_PATTERNS) {
    for (const pattern of config.patterns) {
      const match = trimmedUrl.match(pattern)
      if (match && match[1]) {
        const videoId = match[1]
        return {
          provider: config.provider,
          videoId,
          embedUrl: config.getEmbedUrl(videoId, trimmedUrl),
          thumbnailUrl: config.getThumbnailUrl?.(videoId) || null,
        }
      }
    }
  }

  // Only allow known providers — do NOT embed arbitrary URLs (security: prevents iframe injection)
  return null
}

/**
 * Get just the embed URL from any video URL.
 * @param {string} url - The video URL
 * @returns {string|null}
 */
export const getVideoEmbedUrl = (url) => {
  const parsed = parseVideoUrl(url)
  return parsed?.embedUrl || null
}

/**
 * Get the thumbnail for a video URL (if available without API calls).
 * @param {string} url - The video URL
 * @returns {string|null}
 */
export const getVideoThumbnail = (url) => {
  const parsed = parseVideoUrl(url)
  return parsed?.thumbnailUrl || null
}

export default { parseVideoUrl, getVideoEmbedUrl, getVideoThumbnail }
